import React, { ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'

import { Box } from '../box'
import { PopoverContent } from '../popover'
import { Pressable } from '../pressable'
import { ScrollView } from '../scroll-view'
import { Text } from '../text'

interface DropdownOptionsProps<T> {
    contentRef?: React.Ref<React.ComponentRef<typeof PopoverContent>>
    items: T[]
    selectedLabel?: string
    labelRenderer: (item: T) => string
    valueRenderer?: (item: T) => ReactNode
    onSelect: (item: T) => void
    emptyText?: string
    loading?: boolean
    loadingText?: string
    error?: boolean
    errorText?: string
    width?: number
    maxHeight?: number
}

export function DropdownOptions<T>({
    contentRef,
    items,
    selectedLabel,
    labelRenderer,
    valueRenderer,
    onSelect,
    emptyText,
    loading = false,
    loadingText,
    error = false,
    errorText,
    width,
    maxHeight,
}: DropdownOptionsProps<T>) {
    return (
        <PopoverContent
            ref={contentRef}
            className='w-auto max-w-none rounded border border-solid border-border-subtle bg-aos-bg-elevation py-1'
        >
            {/* ScrollView, not a Box with `overflow-scroll`: RN only honours
                visible/hidden, so a plain View would cap the list and make the
                overflow unreachable on native. */}
            {/* The panel tracks its widest row with the trigger's width as the floor,
                as in MultiSelect and StatusDropdown. A fixed 240px floor here left a
                narrow trigger — e.g. the tag filter in a modal — with a wider panel. */}
            <ScrollView
                style={{
                    ...(width ? { minWidth: width } : {}),
                    ...(maxHeight !== undefined ? { maxHeight } : {}),
                }}
                className='max-h-[240px] self-stretch'
            >
                {loading && (
                    <Box className='px-3 py-2'>
                        <Text size='body' tone='muted'>
                            {loadingText}
                        </Text>
                    </Box>
                )}
                {!loading &&
                    items.map((item, index) => {
                        const isSelected = selectedLabel === labelRenderer(item)
                        return (
                            <Pressable
                                key={index}
                                onPress={() => onSelect(item)}
                                accessibilityRole='menuitem'
                                accessibilityState={{ selected: isSelected }}
                                className={twMerge(
                                    'px-3 py-2',
                                    isSelected
                                        ? 'bg-aos-bg-elevation-hover'
                                        : 'hover:bg-aos-bg-elevation-hover',
                                )}
                            >
                                {valueRenderer ? (
                                    valueRenderer(item)
                                ) : (
                                    <Text size='body' tone='default'>
                                        {labelRenderer(item)}
                                    </Text>
                                )}
                            </Pressable>
                        )
                    })}
                {!loading && error && errorText && (
                    <Box className='px-3 py-2'>
                        <Text size='body' tone='critical'>
                            {errorText}
                        </Text>
                    </Box>
                )}
                {!loading && !error && items.length === 0 && emptyText && (
                    <Box className='px-3 py-2'>
                        <Text size='body' tone='muted'>
                            {emptyText}
                        </Text>
                    </Box>
                )}
            </ScrollView>
        </PopoverContent>
    )
}
